import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router';
import { useTab } from '@ayocore/platform/client/tabs';
import { useLeadRepository, usePartnerRepository } from '../../../rpc';
import { LeadViewController, type LeadViewState } from './LeadViewController';
import type { CompanyInfo } from '@ayocore/erp/crm/common/types/company';
import type { UpdateLead } from '../../../../schemas/lead.schema';

/**
 * Добавляет экшены контроллера к снапшоту состояния для удобного потребления в компонентах.
 */
function withActions(
  controller: LeadViewController,
  snapshot: LeadViewState,
  leadId: number,
  isModal: boolean,
  setTabTitle: (title: string) => void,
  navigate: ReturnType<typeof useNavigate>,
  leadRepository: ReturnType<typeof useLeadRepository>,
  partnerRepository: ReturnType<typeof usePartnerRepository>
) {
  // canEdit на сервере не учитывает архивность (внутренний canUpdate без field
  // должен пускать разархивацию). На клиенте «нет правок архивного лида» — это
  // UX-инвариант: editable-кнопки в карточке прячем. Разархивация идёт отдельной
  // кнопкой в меню заголовка через checkCanArchive → canUpdate(id, 'is_archived').
  const effectivePermissions = snapshot.lead?.is_archived
    ? { ...snapshot.permissions, canEdit: false }
    : snapshot.permissions;

  return {
    // State
    lead: snapshot.lead,
    partner: snapshot.partner,
    contactPersons: snapshot.contactPersons,
    contactChannels: snapshot.contactChannels,
    loading: snapshot.loading,
    error: snapshot.error,
    permissions: effectivePermissions,
    modals: snapshot.modals,

    // Actions
    openEdit: controller.openEdit.bind(controller),
    closeEdit: controller.closeEdit.bind(controller),
    openDelete: controller.openDelete.bind(controller),
    closeDelete: controller.closeDelete.bind(controller),
    deleteLead: () => controller.deleteLead(leadId, navigate, leadRepository),
    checkCanArchive: () => controller.checkCanArchive(leadId, leadRepository),
    archiveLead: () =>
      controller.archiveLead(
        leadId,
        isModal,
        setTabTitle,
        navigate,
        leadRepository,
        partnerRepository
      ),
    unarchiveLead: () =>
      controller.unarchiveLead(
        leadId,
        isModal,
        setTabTitle,
        navigate,
        leadRepository,
        partnerRepository
      ),
    refreshContacts: () => controller.refreshContacts(leadId, leadRepository),
    refreshContactPersons: () => controller.refreshContactPersons(leadId, leadRepository),
    reload: () =>
      controller.reload(
        leadId,
        isModal,
        setTabTitle,
        navigate,
        leadRepository,
        partnerRepository
      ),
    updateLeadField: <K extends keyof UpdateLead>(field: K, value: UpdateLead[K]) =>
      controller.updateLeadField(leadId, field, value, leadRepository, partnerRepository),
    attachPartnerFromDb: (partnerId: number) =>
      controller.attachPartnerToLead(
        leadId,
        partnerId,
        isModal,
        setTabTitle,
        navigate,
        leadRepository,
        partnerRepository
      ),
    createPartnerFromExternal: (company: CompanyInfo) =>
      controller.createPartnerFromExternal(
        leadId,
        company,
        isModal,
        setTabTitle,
        navigate,
        leadRepository,
        partnerRepository
      ),
  };
}

/**
 * useLeadViewController — реактивный хук для работы с LeadView через useSyncExternalStore.
 *
 * Поведение:
 * - Подписывается на `LeadViewController` и возвращает актуальный срез состояния.
 * - Поддерживает селектор: если передан, вернёт выбранный фрагмент; иначе вернёт полный объект
 *   состояния вместе с экшенами.
 * - Внутренне использует стабильный снапшот контроллера (без копирования), что корректно для
 *   concurrent rendering.
 * - Автоматически загружает данные и проверяет права при изменении leadId.
 *
 * Примеры:
 *   const view = useLeadViewController(leadId);                    // полный доступ к state + actions
 *   const lead = useLeadViewController(leadId, s => s.lead);        // только lead
 */
export function useLeadViewController(
  leadId: number,
  isModal?: boolean
): ReturnType<typeof withActions>;
export function useLeadViewController<S>(
  leadId: number,
  isModal: boolean | undefined,
  selector: (full: ReturnType<typeof withActions>) => S
): S;
export function useLeadViewController<S = ReturnType<typeof withActions>>(
  leadId: number,
  isModal: boolean = false,
  selector?: (full: ReturnType<typeof withActions>) => S
): S | ReturnType<typeof withActions> {
  const controllerRef = useRef<LeadViewController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new LeadViewController();
  }
  const controller = controllerRef.current;
  const { setTabTitle } = useTab();
  const navigate = useNavigate();
  const leadRepository = useLeadRepository();
  const partnerRepository = usePartnerRepository();

  // Стабилизируем репозитории через useRef, чтобы избежать бесконечных циклов
  const leadRepoRef = useRef(leadRepository);
  const partnerRepoRef = useRef(partnerRepository);
  const setTabTitleRef = useRef(setTabTitle);
  const navigateRef = useRef(navigate);

  useEffect(() => {
    leadRepoRef.current = leadRepository;
    partnerRepoRef.current = partnerRepository;
    setTabTitleRef.current = setTabTitle;
    navigateRef.current = navigate;
  }, [leadRepository, partnerRepository, setTabTitle, navigate]);

  // Загружаем данные и проверяем права при изменении leadId
  useEffect(() => {
    if (!leadId || isNaN(leadId)) {
      return;
    }

    controller.load(
      leadId,
      isModal,
      setTabTitleRef.current,
      navigateRef.current,
      leadRepoRef.current,
      partnerRepoRef.current
    );
    controller.checkPermissions(leadId, leadRepoRef.current);
  }, [leadId, isModal, controller]);

  // Подписываемся на изменения состояния через useSyncExternalStore
  const state = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot
  );

  // Создаём полный объект с экшенами
  const full = useMemo(
    () =>
      withActions(
        controller,
        state,
        leadId,
        isModal,
        setTabTitle,
        navigate,
        leadRepository,
        partnerRepository
      ),
    [
      controller,
      state,
      leadId,
      isModal,
      setTabTitle,
      navigate,
      leadRepository,
      partnerRepository,
    ]
  );

  // Если селектор не передан, возвращаем полный объект
  if (!selector) return full;

  // Если селектор передан, применяем его с мемоизацией
  const selectedRef = useRef<S | undefined>(undefined);
  return useMemo(() => {
    const selected = selector(full);
    if (selectedRef.current !== undefined) {
      try {
        if (JSON.stringify(selectedRef.current) === JSON.stringify(selected)) {
          return selectedRef.current;
        }
      } catch {
        /* noop */
      }
    }
    selectedRef.current = selected;
    return selected;
  }, [full, selector]);
}
